/**
 * Header search: build a flat list from /event-batches/upcoming/treks rows, then filter in the browser.
 */

function plainTextFromMaybeHtml(s) {
  return String(s || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function buildHeaderSearchItemsFromRows(rows) {
  const seen = new Set();
  const items = [];
  if (!Array.isArray(rows)) return items;
  for (const row of rows) {
    const trek = row?.trek || {};
    const slug = trek.slug;
    if (!slug || seen.has(slug)) continue;
    seen.add(slug);
    items.push({
      slug,
      title: trek.name || row?.nextBatch?.code || 'Event',
      location: trek.location || '',
      tagline: plainTextFromMaybeHtml(trek.tagline),
      description: plainTextFromMaybeHtml(trek.description),
      startDate: row?.nextBatch?.startDate ? String(row.nextBatch.startDate).split('T')[0] : null
    });
  }
  return items;
}

/** Variants for common typos (each token matches if any variant is in haystack). */
function tokenVariants(token) {
  const t = String(token).toLowerCase();
  const set = new Set([t]);
  if (t === 'pas') set.add('pass');
  return [...set];
}

function haystackForItem(item) {
  return [item.title, item.location, item.slug, item.tagline, item.description]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function scoreMatch(item, normalizedQuery, tokens) {
  const title = String(item.title || '').toLowerCase();
  const hay = haystackForItem(item);
  if (title.includes(normalizedQuery)) return 100;
  if (normalizedQuery.length >= 3 && hay.includes(normalizedQuery)) return 80;
  const allInTitle = tokens.every((toks) => toks.some((v) => title.includes(v)));
  if (allInTitle) return 60;
  return 40;
}

/**
 * @param {Array<{ slug: string, title: string, location?: string, tagline?: string, description?: string, startDate?: string|null }>} items
 * @param {string} rawQuery
 */
export function filterHeaderSearchItems(items, rawQuery) {
  const q = String(rawQuery || '').trim().toLowerCase();
  if (q.length < 2) return [];

  const tokenStrs = q.split(/\s+/u).filter(Boolean);
  const tokenVariantLists = tokenStrs.map(tokenVariants);

  const matches = items.filter((item) => {
    const hay = haystackForItem(item);
    if (hay.includes(q)) return true;
    return tokenVariantLists.every((variants) => variants.some((v) => hay.includes(v)));
  });

  matches.sort(
    (a, b) =>
      scoreMatch(b, q, tokenVariantLists) - scoreMatch(a, q, tokenVariantLists) ||
      String(a.title || '').localeCompare(String(b.title || ''))
  );

  return matches;
}

/** Events page: filter upcoming trek cards using the same rules as the header search. */
export function upcomingTrekCardMatchesSearch(card, rawQuery) {
  const q = String(rawQuery || '').trim();
  if (q.length === 0) return true;
  /* Header uses filterHeaderSearchItems only for terms with length >= 2; shorter terms match nothing. */
  if (q.length < 2) return false;
  const trek = card?.trek || {};
  const item = {
    slug: String(trek.slug || ''),
    title: String(trek.name || ''),
    location: String(trek.location || ''),
    tagline: plainTextFromMaybeHtml(trek.tagline),
    description: plainTextFromMaybeHtml(trek.description)
  };
  return filterHeaderSearchItems([item], q).length > 0;
}
